import React, { useState, useRef, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TextInput,
    TouchableOpacity,
    StatusBar,
    Platform,
    KeyboardAvoidingView,
    Image,
    ActivityIndicator,
    Linking,
    Alert,
    ActionSheetIOS,
    Keyboard,
    Modal,
    Pressable,
    Dimensions,
    Animated,
} from 'react-native';
import { launchImageLibrary, launchCamera, Asset } from 'react-native-image-picker';
import Clipboard from '@react-native-clipboard/clipboard';
import TabBar from '../components/TabBar';
import DashboardHeader from '../components/DashboardHeader';
import { useChat } from '../contexts/ChatContext';
import { V2ChatMessage } from '../types/v2';
import { SkeletonMessageBubble } from '../components/Skeleton';
import BackButton from '../components/UIComponents/BackButton';
import { COLORS } from '../theme/themeTokens';
import { COLORS_WRONG } from '../theme/colorsWrong';
import { consumeSupportPrefill, type SupportRequestReference } from '../tools/supportPrefill';
import { useAuth } from '../contexts/AuthContext';
import { v2GetMyPushNotifications } from '../services/v2/api';
import { useCapabilities } from '../hooks/useCapabilities';

// Chat limits
const LIMITS = {
    MESSAGE_MAX_LENGTH: 4000, // ~700 words
    FILE_MAX_SIZE_MB: 10,
    FILE_MAX_SIZE_BYTES: 10 * 1024 * 1024, // 10 MB
};

// Format timestamp for display
const formatMessageTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
    });
};

// formatMessageDate returns a day-level label for the date separator shown
// between groups of messages from different days: "Today" / "Yesterday" /
// "15 April" (current year) / "15 April 2025" (older).
const formatMessageDate = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
    });
};

const isSameDay = (a: string, b: string): boolean => {
    const ad = new Date(a);
    const bd = new Date(b);
    return ad.getFullYear() === bd.getFullYear()
        && ad.getMonth() === bd.getMonth()
        && ad.getDate() === bd.getDate();
};

// Centred day-label that appears between chronological groups of messages.
const DateSeparator = ({ label }: { label: string }) => (
    <View style={styles.dateSeparator}>
        <Text style={styles.dateSeparatorText}>{label}</Text>
    </View>
);

/** Координаты bubble в окне для позиционирования popup-меню рядом с ним. */
export interface BubbleRect {
    x: number;       // абсолютная X левого края bubble
    y: number;       // абсолютная Y верхнего края bubble
    width: number;   // ширина bubble
    height: number;  // высота bubble
}

// Message Bubble Component
const MessageBubble = ({
    message,
    onActionsRequested,
}: {
    message: V2ChatMessage;
    /** Тап по своему не-удалённому сообщению → передаём в parent сообщение
     *  + измеренный rect самого bubble (screen-абсолютные координаты).
     *  Parent позиционирует popup-меню рядом. Для входящих/удалённых не
     *  передаём — тап игнорируется. */
    onActionsRequested?: (message: V2ChatMessage, rect: BubbleRect) => void;
}) => {
    const isOutgoing = message.sender_type === 'user';
    const isDeleted = !!message.deleted_at;
    const isInteractive = isOutgoing && !isDeleted && !!onActionsRequested;
    const bubbleRef = useRef<View>(null);
    // Tiny horizontal "wiggle" played on tap — gives the bubble a
    // tactile feedback that's less aggressive than haptic vibration.
    // Amplitude stays under 4 dp so the bubble visibly moves but
    // doesn't shake violently.
    const shakeAnim = useRef(new Animated.Value(0)).current;
    const playShake = () => {
        Animated.sequence([
            Animated.timing(shakeAnim, { toValue: -3, duration: 40, useNativeDriver: true }),
            Animated.timing(shakeAnim, { toValue: 3,  duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnim, { toValue: -2, duration: 40, useNativeDriver: true }),
            Animated.timing(shakeAnim, { toValue: 0,  duration: 40, useNativeDriver: true }),
        ]).start();
    };

    const handleAttachmentPress = () => {
        if (message.attachment_url) {
            Linking.openURL(message.attachment_url);
        }
    };

    const handlePress = () => {
        if (!isInteractive) return;
        playShake();
        // measureInWindow → screen-абсолютные координаты с учётом всех scroll'ов
        // и обвёрток (status bar, header, KeyboardAvoidingView). Это то, что
        // нужно для popup'а внутри отдельного Modal'а — он рисуется в корне.
        bubbleRef.current?.measureInWindow((x, y, width, height) => {
            onActionsRequested?.(message, { x, y, width, height });
        });
    };

    const InteractiveWrap = isInteractive ? TouchableOpacity : View;

    return (
        <View
            style={[
                styles.messageContainer,
                isOutgoing ? styles.outgoingContainer : styles.incomingContainer,
            ]}>
            <Animated.View style={{ transform: [{ translateX: shakeAnim }] }}>
            <InteractiveWrap
                ref={bubbleRef as React.Ref<any>}
                {...(isInteractive
                    ? { activeOpacity: 0.7, onPress: handlePress }
                    : {})}
                style={[
                    styles.bubble,
                    isOutgoing ? styles.outgoingBubble : styles.incomingBubble,
                ]}>
                {message.content && (
                    <Text
                        style={[
                            styles.messageText,
                            isOutgoing ? styles.outgoingText : styles.incomingText,
                        ]}>
                        {message.content}
                    </Text>
                )}
                {message.attachment_filename && (
                    <TouchableOpacity onPress={handleAttachmentPress} style={styles.attachmentContainer}>
                        <Text style={[
                            styles.attachmentText,
                            isOutgoing ? styles.outgoingText : styles.incomingText,
                        ]}>
                            📎 {message.attachment_filename}
                        </Text>
                    </TouchableOpacity>
                )}
                <Text
                    style={[
                        styles.timeText,
                        isOutgoing ? styles.outgoingTime : styles.incomingTime,
                    ]}>
                    {formatMessageTime(message.created_at)}
                    {message.edited_at && ' · edited'}
                </Text>
            </InteractiveWrap>
            </Animated.View>
        </View>
    );
};

// Tapping anywhere on an empty conversation leaves the composer — there is
// no message list here to dismiss the keyboard by scrolling. Only these two
// states are wrapped: a tap handler around the whole KeyboardAvoidingView
// would steal the first scroll gesture from the message list (see below).
const DismissKeyboardArea: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Pressable style={styles.dismissArea} onPress={Keyboard.dismiss} accessible={false}>
        {children}
    </Pressable>
);

// Loading skeleton for messages
const MessagesSkeleton = () => (
    <View style={styles.skeletonContainer}>
        <SkeletonMessageBubble isOutgoing={false} />
        <SkeletonMessageBubble isOutgoing={true} />
        <SkeletonMessageBubble isOutgoing={false} />
    </View>
);

// Empty state component
const EmptyState = () => (
    <View style={styles.emptyContainer}>
        <Text style={styles.emptyTitle}>Welcome to Support</Text>
        <Text style={styles.emptySubtitle}>
            Send us a message and we'll get back to you as soon as possible.
        </Text>
    </View>
);

interface SupportScreenProps {
    onNavigate: (screen: string) => void;
    hideHeader?: boolean;
}

interface SelectedFile {
    uri: string;
    name: string;
    type: string;
}

const SupportScreen: React.FC<SupportScreenProps> = ({ onNavigate, hideHeader = false }) => {
    const statusBarHeight = Platform.OS === 'android' ? 28 : 20;
    // Высота должна совпадать с реальной высотой TabBar (см.
    // src/components/TabBar.tsx → styles.tabBar.height). На Android там 80
    // (20 dp учитывают gesture nav bar), на iOS 65. Если эти числа
    // разойдутся — input уезжает под TabBar на разницу.
    const tabBarHeight = Platform.OS !== 'android' ? 65 : 80;
    const scrollViewRef = useRef<ScrollView>(null);
    const [messageText, setMessageText] = useState('');
    const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
    const [requestReference, setRequestReference] = useState<SupportRequestReference | null>(null);
    const { user } = useAuth();
    const caps = useCapabilities();
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    const [showEqvTooltip, setShowEqvTooltip] = useState(false);

    const userLogin = user?.login || '-----';

    const {
        messages,
        isLoading,
        isSending,
        sendMessage,
        sendMessageWithAttachment,
        editMessage,
        deleteMessage,
        setChatActive,
    } = useChat();

    // ── Action-menu state ──
    // actionMenuMessage != null → открыто меню действий рядом с bubble'ом.
    // actionMenuRect хранит позицию того bubble'а в окне, popup позиционируется
    // вверх или вниз от неё (вверх если bubble близко к низу экрана).
    // editingMessage != null  → открыта модалка редактирования (после Edit).
    const [actionMenuMessage, setActionMenuMessage] = useState<V2ChatMessage | null>(null);
    const [actionMenuRect, setActionMenuRect] = useState<BubbleRect | null>(null);
    const [editingMessage, setEditingMessage] = useState<V2ChatMessage | null>(null);
    const [editText, setEditText] = useState('');
    const [editSaving, setEditSaving] = useState(false);

    const openActionMenu = (m: V2ChatMessage, rect: BubbleRect) => {
        setActionMenuMessage(m);
        setActionMenuRect(rect);
    };

    const closeActionMenu = () => {
        setActionMenuMessage(null);
        setActionMenuRect(null);
    };

    const handleActionEdit = () => {
        if (!actionMenuMessage) return;
        const m = actionMenuMessage;
        setActionMenuMessage(null);
        setEditingMessage(m);
        setEditText(m.content ?? '');
    };

    const handleActionCopy = () => {
        if (!actionMenuMessage) return;
        const text = actionMenuMessage.content ?? '';
        Clipboard.setString(text);
        setActionMenuMessage(null);
    };

    const handleActionDelete = () => {
        if (!actionMenuMessage) return;
        const m = actionMenuMessage;
        setActionMenuMessage(null);
        // Confirmation alert — стандартный паттерн перед destructive action.
        Alert.alert(
            'Delete message',
            'This will replace the message with a "deleted" tombstone for both you and support. This cannot be undone.',
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Delete',
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            await deleteMessage(m.id);
                        } catch (err) {
                            const reason = err instanceof Error ? err.message : 'Unknown error';
                            Alert.alert('Delete failed', reason);
                        }
                    },
                },
            ],
        );
    };

    const closeEditModal = () => {
        if (editSaving) return;
        setEditingMessage(null);
        setEditText('');
    };

    const handleEditSave = async () => {
        if (!editingMessage) return;
        const trimmed = editText.trim();
        if (!trimmed || trimmed === (editingMessage.content ?? '')) {
            closeEditModal();
            return;
        }
        setEditSaving(true);
        try {
            await editMessage(editingMessage.id, trimmed);
            setEditingMessage(null);
            setEditText('');
        } catch (err) {
            const reason = err instanceof Error ? err.message : 'Unknown error';
            Alert.alert('Edit failed', reason);
        } finally {
            setEditSaving(false);
        }
    };

    // Mark chat as active when screen opens, inactive when leaving
    useEffect(() => {
        setChatActive(true);
        return () => {
            setChatActive(false);
        };
    }, [setChatActive]);

    // Fetch unread notifications status (for bell icon)
    useEffect(() => {
        (async () => {
            try {
                const res = await v2GetMyPushNotifications();
                const unread = (res.notifications || []).some((n: any) => !n.viewed_at);
                setHasUnreadNotifications(unread);
            } catch {}
        })();
    }, []);

    // Consume any pending prefill message on mount
    useEffect(() => {
        const prefill = consumeSupportPrefill();
        if (prefill.message) {
            setMessageText(prefill.message);
        }
        setRequestReference(prefill.request);
    }, []);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (messages.length > 0) {
            setTimeout(() => {
                scrollViewRef.current?.scrollToEnd({ animated: true });
            }, 100);
        }
    }, [messages.length]);

    const handleSend = async () => {
        if (isSending) return;

        const content = messageText.trim();
        const hasContent = content.length > 0;
        const hasFile = selectedFile !== null;

        if (!hasContent && !hasFile) return;

        const requestPrefix = requestReference
            ? `Order ID: ${String(requestReference.number).padStart(8, '0')}`
            : '';
        const outgoingContent = [requestPrefix, content].filter(Boolean).join('\n');

        // The text and the file stay in the composer until the message is
        // sent: clearing them first lost both whenever the send failed.
        let success = false;

        if (hasFile && selectedFile) {
            // Send message with attachment
            success = await sendMessageWithAttachment(
                outgoingContent || null,
                {
                    uri: selectedFile.uri,
                    name: selectedFile.name,
                    type: selectedFile.type,
                }
            );
        } else {
            // Send text-only message
            success = await sendMessage(outgoingContent);
        }

        if (!success) {
            Alert.alert('Message not sent', 'Your message is kept below. Please try again.');
            return;
        }
        // Clear only what was sent.
        setMessageText((current) => (current.trim() === content ? '' : current));
        setSelectedFile((current) => (current === selectedFile ? null : current));
        setRequestReference(null);
        setTimeout(() => {
            scrollViewRef.current?.scrollToEnd({ animated: true });
        }, 100);
    };

    const processAsset = (asset: Asset) => {
        if (!asset.uri) return;

        // Check file size limit
        if (asset.fileSize && asset.fileSize > LIMITS.FILE_MAX_SIZE_BYTES) {
            Alert.alert(
                'File too large',
                `Please select a file smaller than ${LIMITS.FILE_MAX_SIZE_MB}MB`
            );
            return;
        }

        setSelectedFile({
            uri: asset.uri,
            name: asset.fileName || `image_${Date.now()}.jpg`,
            type: asset.type || 'image/jpeg',
        });
    };

    // Handle text input with character limit
    const handleTextChange = (text: string) => {
        const requestPrefixLength = requestReference
            ? `Order ID: ${String(requestReference.number).padStart(8, '0')}\n`.length
            : 0;
        if (text.length <= LIMITS.MESSAGE_MAX_LENGTH - requestPrefixLength) {
            setMessageText(text);
        }
    };

    // Character count display
    const requestPrefixLength = requestReference
        ? `Order ID: ${String(requestReference.number).padStart(8, '0')}\n`.length
        : 0;
    const messageMaxLength = LIMITS.MESSAGE_MAX_LENGTH - requestPrefixLength;
    const charCount = messageText.length;
    const isNearLimit = charCount > messageMaxLength * 0.9; // 90% of limit
    const isAtLimit = charCount >= messageMaxLength;

    const handleAttachment = () => {
        // The browser's own file picker already offers the library, the camera and files.
        if (Platform.OS === 'web') {
            handleImageLibrary();
            return;
        }
        if (Platform.OS === 'ios') {
            ActionSheetIOS.showActionSheetWithOptions(
                {
                    options: ['Cancel', 'Take Photo', 'Choose from Library'],
                    cancelButtonIndex: 0,
                },
                (buttonIndex) => {
                    if (buttonIndex === 1) {
                        handleCamera();
                    } else if (buttonIndex === 2) {
                        handleImageLibrary();
                    }
                }
            );
        } else {
            // For Android, show an Alert with options
            Alert.alert(
                'Add Attachment',
                'Choose an option',
                [
                    { text: 'Cancel', style: 'cancel' },
                    { text: 'Take Photo', onPress: handleCamera },
                    { text: 'Choose from Library', onPress: handleImageLibrary },
                ]
            );
        }
    };

    const handleCamera = async () => {
        try {
            const result = await launchCamera({
                mediaType: 'mixed',
                quality: 0.8,
            });

            if (result.assets && result.assets.length > 0) {
                processAsset(result.assets[0]);
            }
        } catch (err) {
            console.error('Error launching camera:', err);
        }
    };

    const handleImageLibrary = async () => {
        try {
            const result = await launchImageLibrary({
                mediaType: 'mixed',
                quality: 0.8,
            });

            if (result.assets && result.assets.length > 0) {
                processAsset(result.assets[0]);
            }
        } catch (err) {
            console.error('Error launching image library:', err);
        }
    };

    const clearSelectedFile = () => {
        setSelectedFile(null);
    };

    return (
        <View style={styles.container}>
            <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

            {/* Header */}
            {!hideHeader && (
                <DashboardHeader
                    onNavigate={onNavigate}
                    hasUnreadNotifications={hasUnreadNotifications}
                    variant="solid"
                />
            )}

            {/* Main Content */}
            {/*
             * Note: do NOT wrap KeyboardAvoidingView in TouchableWithoutFeedback —
             * the cloned tap handler steals the first scroll gesture from the
             * messages ScrollView. Keyboard dismissal is already covered by
             * ScrollView's onScrollBeginDrag + keyboardShouldPersistTaps below.
             */}
            <KeyboardAvoidingView
                style={styles.keyboardAvoid}
                // iOS: 'padding' добавляет нижний отступ под клавиатуру —
                //   стандартный путь, работает корректно.
                // Android: behavior={undefined} → ОС сама ресайзит layout
                //   через windowSoftInputMode=adjustResize (стандарт в RN
                //   шаблоне). KAV в этом случае ничего не делает, инпут
                //   плавно остаётся над клавиатурой, как на iOS, без
                //   двойного offset'а который раньше «подбрасывал» поле.
                behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                keyboardVerticalOffset={0}
            >

                {/* Messages */}
                {isLoading ? (
                    <DismissKeyboardArea><MessagesSkeleton /></DismissKeyboardArea>
                ) : messages.length === 0 ? (
                    <DismissKeyboardArea><EmptyState /></DismissKeyboardArea>
                ) : (
                    <ScrollView
                        ref={scrollViewRef}
                        style={styles.messagesContainer}
                        contentContainerStyle={styles.messagesContent}
                        showsVerticalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled"
                        onScrollBeginDrag={Keyboard.dismiss}>
                        {messages.map((message, idx) => {
                            const showDate = idx === 0
                                || !isSameDay(messages[idx - 1].created_at, message.created_at);
                            return (
                                <React.Fragment key={message.id}>
                                    {showDate && <DateSeparator label={formatMessageDate(message.created_at)} />}
                                    <MessageBubble message={message} onActionsRequested={openActionMenu} />
                                </React.Fragment>
                            );
                        })}
                    </ScrollView>
                )}

                {/* Selected File Preview */}
                {selectedFile && (
                    <View style={styles.selectedFileContainer}>
                        <View style={styles.selectedFileInfo}>
                            <Text style={styles.selectedFileIcon}>📎</Text>
                            <Text style={styles.selectedFileName} numberOfLines={1}>
                                {selectedFile.name}
                            </Text>
                        </View>
                        <TouchableOpacity onPress={clearSelectedFile} style={styles.clearFileButton}>
                            <Text style={styles.clearFileText}>✕</Text>
                        </TouchableOpacity>
                    </View>
                )}

                {requestReference && (
                    <View style={styles.requestReferenceContainer}>
                        <View style={styles.requestReferenceContent}>
                            <Text style={styles.requestReferenceCaption}>Linked order</Text>
                            <Text style={styles.requestReferenceNumber}>
                                #{String(requestReference.number).padStart(8, '0')}
                            </Text>
                        </View>
                        <TouchableOpacity
                            onPress={() => setRequestReference(null)}
                            style={styles.clearRequestReferenceButton}
                            accessibilityRole="button"
                            accessibilityLabel="Remove linked order">
                            <Text style={styles.clearRequestReferenceText}>✕</Text>
                        </TouchableOpacity>
                    </View>
                )}

                {/* Character Counter */}
                {isNearLimit && (
                    <View style={styles.charCountContainer}>
                        <Text style={[
                            styles.charCountText,
                            isAtLimit && styles.charCountLimit,
                        ]}>
                            {charCount}/{messageMaxLength}
                        </Text>
                    </View>
                )}

                {/* Input Area */}
                <View style={styles.inputContainer}>
                    <View style={styles.inputWrapper}>
                        <TouchableOpacity
                            style={[styles.attachButton, selectedFile && styles.attachButtonActive]}
                            onPress={handleAttachment}
                            disabled={isSending}>
                            <Image
                                source={require('../assets/support-attach.png')}
                                style={[styles.attachIcon, selectedFile && styles.attachIconActive]}
                                resizeMode="contain"
                            />
                        </TouchableOpacity>
                        <TextInput
                            style={styles.textInput}
                            placeholder="Message"
                            placeholderTextColor={COLORS.blueSteel}
                            value={messageText}
                            onChangeText={handleTextChange}
                            multiline
                            // A <textarea> is two rows high by default, taller than the send button.
                            rows={1}
                            editable={!isSending}
                            maxLength={messageMaxLength}
                        />
                        <TouchableOpacity
                            style={[
                                styles.sendButton,
                                (isSending || !caps.can_send_chat_message) && styles.sendButtonDisabled,
                            ]}
                            onPress={handleSend}
                            disabled={
                                isSending ||
                                (!messageText.trim() && !selectedFile) ||
                                !caps.can_send_chat_message
                            }>
                            {isSending ? (
                                <ActivityIndicator color={COLORS.white} size="small" />
                            ) : (
                                <Image
                                    source={require('../assets/support-send.png')}
                                    style={styles.sendIcon}
                                    resizeMode="contain"
                                />
                            )}
                        </TouchableOpacity>
                    </View>
                </View>

                {/* Spacer so content clears the absolutely-positioned TabBar */}
                <View style={{ height: tabBarHeight }} />
                
            </KeyboardAvoidingView>

            {/* Tab Bar */}
            <TabBar activeTab="support" onNavigate={onNavigate} />

            {/* ── Context popup menu ──
                Маленький popup рядом с bubble'ом (а не bottom-sheet).
                Позиционируется сверху bubble если внизу мало места, иначе снизу.
                Координаты bubble приходят из MessageBubble.measureInWindow. */}
            <Modal
                visible={actionMenuMessage != null && actionMenuRect != null}
                transparent
                animationType="fade"
                onRequestClose={closeActionMenu}
            >
                <TouchableOpacity
                    style={styles.popupOverlay}
                    activeOpacity={1}
                    onPress={closeActionMenu}
                >
                    {actionMenuRect && (() => {
                        const POPUP_WIDTH = 180;
                        const POPUP_HEIGHT = 132; // 3 items × 44px = 132
                        const GAP = 6;
                        const screen = Dimensions.get('window');
                        // Вертикально: пробуем разместить снизу bubble; если не
                        // влезает — сверху.
                        const belowSpace = screen.height - (actionMenuRect.y + actionMenuRect.height);
                        const placeBelow = belowSpace >= POPUP_HEIGHT + GAP + 20;
                        const top = placeBelow
                            ? actionMenuRect.y + actionMenuRect.height + GAP
                            : actionMenuRect.y - POPUP_HEIGHT - GAP;
                        // Горизонтально: выровнено по правому краю bubble (свои
                        // сообщения справа), но не вылезает за экран слева.
                        const rightEdge = actionMenuRect.x + actionMenuRect.width;
                        let left = rightEdge - POPUP_WIDTH;
                        if (left < 8) left = 8;
                        if (left + POPUP_WIDTH > screen.width - 8) {
                            left = screen.width - POPUP_WIDTH - 8;
                        }
                        return (
                            <TouchableOpacity
                                activeOpacity={1}
                                onPress={() => { /* stop propagation */ }}
                                style={[
                                    styles.popupCard,
                                    { top, left, width: POPUP_WIDTH },
                                ]}
                            >
                                <TouchableOpacity style={styles.popupItem} onPress={handleActionEdit}>
                                    <Text style={styles.popupItemText}>Edit</Text>
                                </TouchableOpacity>
                                <View style={styles.popupDivider} />
                                <TouchableOpacity style={styles.popupItem} onPress={handleActionCopy}>
                                    <Text style={styles.popupItemText}>Copy</Text>
                                </TouchableOpacity>
                                <View style={styles.popupDivider} />
                                <TouchableOpacity style={styles.popupItem} onPress={handleActionDelete}>
                                    <Text style={[styles.popupItemText, styles.popupItemTextDanger]}>
                                        Delete
                                    </Text>
                                </TouchableOpacity>
                            </TouchableOpacity>
                        );
                    })()}
                </TouchableOpacity>
            </Modal>

            {/* ── Edit message modal ──
                Открывается long-press'ом на исходящем не-удалённом сообщении.
                Сохраняет через ChatContext.editMessage → optimistic
                local update; audit-таблица фиксирует old-image на бэке. */}
            <Modal
                visible={editingMessage != null}
                transparent
                animationType="fade"
                onRequestClose={closeEditModal}
            >
                <TouchableOpacity
                    style={styles.editModalOverlay}
                    activeOpacity={1}
                    onPress={closeEditModal}
                >
                    <TouchableOpacity
                        activeOpacity={1}
                        onPress={() => {/* свайп-стоп, чтобы тап внутри диалога не закрывал */}}
                        style={styles.editModalContainer}
                    >
                        <Text style={styles.editModalTitle}>Edit message</Text>
                        <TextInput
                            style={styles.editModalInput}
                            value={editText}
                            onChangeText={setEditText}
                            multiline
                            autoFocus
                            maxLength={1000}
                            placeholder="Message"
                            editable={!editSaving}
                        />
                        <View style={styles.editModalActions}>
                            <TouchableOpacity
                                style={styles.editModalBtnCancel}
                                onPress={closeEditModal}
                                disabled={editSaving}
                            >
                                <Text style={styles.editModalBtnCancelText}>Cancel</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[
                                    styles.editModalBtnSave,
                                    (editSaving || !editText.trim()) && styles.editModalBtnSaveDisabled,
                                ]}
                                onPress={handleEditSave}
                                disabled={editSaving || !editText.trim()}
                            >
                                {editSaving
                                    ? <ActivityIndicator size="small" color="#fff" />
                                    : <Text style={styles.editModalBtnSaveText}>Save</Text>
                                }
                            </TouchableOpacity>
                        </View>
                    </TouchableOpacity>
                </TouchableOpacity>
            </Modal>
        </View>
    );
};

const styles = StyleSheet.create({

    keyboardAvoid: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    marginTop: 10,
    overflow: 'hidden',

  },
    container: {
        flex: 1,
        backgroundColor: COLORS.primaryNavy,
    },
    headerBackground: {
        backgroundColor: COLORS.primaryNavy,
        paddingBottom: 20,
    },
    supportHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
    },
    headerIconBtn: {
        width: 40,
        height: 40,
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerIcon: {
        width: 28,
        height: 28,
        tintColor: COLORS.white,
    },
    headerLogo: {
        width: 100,
        height: 28,
    },
    headerRightGroup: {
        flexDirection: 'row' as const,
        alignItems: 'center' as const,
    },
    headerLogin: {
        fontFamily: 'Inter',
        fontSize: 16,
        fontWeight: '400',
        lineHeight: 22,
        color: COLORS.white,
        textAlign: 'center',
    },
    headerTitleRow: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitleCol: {
        alignItems: 'center',
    },
    headerTitleInner: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    balanceLabelIcon: {
        width: 13,
        height: 13,
        marginLeft: 2,
        marginBottom: 11,
    },
    eqvTooltipText: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: 13,
        fontFamily: 'Inter',
    },
    mainContent: {
        flex: 1,
        backgroundColor: COLORS.white,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        marginTop: -10,
    },
    messagesContainer: {
        flex: 1,
    },
    messagesContent: {
        padding: 20,
        paddingBottom: 10,
    },
    dismissArea: {
        flex: 1,
    },
    skeletonContainer: {
        flex: 1,
        padding: 20,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
    },
    emptyTitle: {
        fontSize: 22,
        fontWeight: '600',
        color: COLORS.steelNavy,
        marginBottom: 8,
    },
    emptySubtitle: {
        fontSize: 16,
        color: COLORS.blueSteel,
        textAlign: 'center',
        lineHeight: 22,
    },
    messageContainer: {
        marginBottom: 12,
        gap: 12,
    },
    incomingContainer: {
        alignItems: 'flex-start',
        maxWidth: '80%',
    },
    outgoingContainer: {
        alignItems: 'flex-end',
        alignSelf: 'flex-end',
        maxWidth: '80%',
    },
    bubble: {
        borderRadius: 12,
        paddingHorizontal: 10,
        paddingVertical: 8,
    },
    incomingBubble: {
        backgroundColor: COLORS.softGrey,
    },
    outgoingBubble: {
        backgroundColor: COLORS.steelNavy,
    },
    messageText: {
        fontSize: 18,
        lineHeight: 22,
        fontFamily: 'Montserrat',
        fontWeight: '400',
    },
    incomingText: {
        color: COLORS.steelNavy,
    },
    outgoingText: {
        color: COLORS.pureLight,
    },
    attachmentContainer: {
        marginTop: 8,
        paddingTop: 8,
        borderTopWidth: 1,
        borderTopColor: COLORS_WRONG.whiteFaded,
    },
    attachmentText: {
        fontSize: 16,
        textDecorationLine: 'underline',
    },
    timeText: {
        fontSize: 13,
        lineHeight: 16,
        fontFamily: 'Montserrat',
        fontWeight: '400',
        marginTop: 4,
    },
    dateSeparator: {
        alignSelf: 'center',
        marginVertical: 14,
        paddingHorizontal: 14,
        paddingVertical: 6,
        backgroundColor: 'rgba(31, 52, 75, 0.08)',
        borderRadius: 12,
    },
    dateSeparatorText: {
        fontSize: 12,
        lineHeight: 16,
        fontFamily: 'Montserrat',
        fontWeight: '600',
        color: COLORS.blueSteel,
    },
    incomingTime: {
        alignSelf: 'flex-start',
        color: COLORS.blueSteel,
    },
    outgoingTime: {
        alignSelf: 'flex-end',
        color: 'rgba(255,255,255,0.6)',
    },
    /* Composer — the one clients liked in Client support
       (Support/SupportClientChatScreen): a white strip with a hairline on
       top, the field in its own rounded box and a filled send button. */
    inputContainer: {
        paddingBottom: 0,
    },
    inputWrapper: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        padding: 10,
        backgroundColor: COLORS.white,
        borderTopWidth: 1,
        borderTopColor: COLORS.softGrey,
    },
    textInput: {
        flex: 1,
        minHeight: 40,
        maxHeight: 110,
        borderWidth: 1,
        borderColor: COLORS.softGrey,
        borderRadius: 8,
        fontSize: 16,
        fontFamily: 'Inter',
        fontWeight: '400',
        color: COLORS.primaryNavy,
        paddingHorizontal: 10,
        paddingVertical: 8,
    },
    attachButton: {
        width: 36,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    attachIcon: {
        width: 20,
        height: 20,
        tintColor: COLORS.blueSteel,
    },
    sendButton: {
        width: 42,
        height: 40,
        marginLeft: 8,
        borderRadius: 8,
        backgroundColor: COLORS.primaryNavy,
        justifyContent: 'center',
        alignItems: 'center',
    },
    sendButtonDisabled: {
        opacity: 0.4,
    },
    sendIcon: {
        width: 18,
        height: 18,
        tintColor: COLORS.white,
    },
    selectedFileContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginHorizontal: 20,
        marginBottom: 8,
        padding: 12,
        backgroundColor: COLORS.pureLight,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: COLORS.steelNavy,
    },
    selectedFileInfo: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    selectedFileIcon: {
        fontSize: 18,
    },
    selectedFileName: {
        flex: 1,
        fontSize: 16,
        color: COLORS.steelNavy,
        fontWeight: '500',
    },
    clearFileButton: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: COLORS.blueSteel,
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 8,
    },
    clearFileText: {
        color: COLORS.white,
        fontSize: 14,
        fontWeight: '600',
    },
    requestReferenceContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginHorizontal: 20,
        marginBottom: 8,
        paddingHorizontal: 14,
        minHeight: 48,
        backgroundColor: '#EEF3F8',
        borderRadius: 10,
        borderWidth: 1,
        borderColor: COLORS.borderMuted,
    },
    requestReferenceContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    requestReferenceCaption: {
        color: COLORS.slate,
        fontSize: 13,
        fontFamily: 'Inter',
        fontWeight: '500',
    },
    requestReferenceNumber: {
        color: COLORS.primaryNavy,
        fontSize: 14,
        fontFamily: 'Inter',
        fontWeight: '700',
    },
    clearRequestReferenceButton: {
        width: 30,
        height: 30,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 15,
    },
    clearRequestReferenceText: {
        color: COLORS.slate,
        fontSize: 16,
        fontWeight: '500',
    },
    attachButtonActive: {
        backgroundColor: COLORS_WRONG.backgroundFaded,
        borderRadius: 4,
    },
    attachIconActive: {
        tintColor: COLORS.steelNavy,
    },
    charCountContainer: {
        paddingHorizontal: 20,
        paddingBottom: 4,
        alignItems: 'flex-end',
    },
    charCountText: {
        fontSize: 14,
        color: COLORS.blueSteel,
        fontFamily: 'Inter',
    },
    charCountLimit: {
        color: COLORS_WRONG.error,
        fontWeight: '600',
    },

    // Context popup — маленькое всплывающее меню рядом с bubble'ом.
    popupOverlay: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    popupCard: {
        position: 'absolute',
        backgroundColor: COLORS.pureLight ?? '#fff',
        borderRadius: 10,
        // Лёгкая тень — отделяем popup от bubble'а ниже.
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.18,
        shadowRadius: 12,
        elevation: 8,
        overflow: 'hidden',
    },
    popupItem: {
        paddingVertical: 12,
        paddingHorizontal: 14,
    },
    popupItemText: {
        fontFamily: 'Inter',
        fontSize: 14,
        fontWeight: '500',
        color: COLORS.deepNavy ?? '#1c2228',
    },
    popupItemTextDanger: {
        color: '#E53935',
    },
    popupDivider: {
        height: 1,
        backgroundColor: '#e1e4eb',
        marginHorizontal: 12,
    },

    // Edit-message modal — паттерн копирует PINConfirmModal.
    editModalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    editModalContainer: {
        width: '100%',
        maxWidth: 360,
        backgroundColor: COLORS.pureLight ?? '#fff',
        borderRadius: 16,
        padding: 20,
    },
    editModalTitle: {
        fontFamily: 'Inter',
        fontSize: 18,
        fontWeight: '600',
        color: COLORS.deepNavy ?? '#1c2228',
        marginBottom: 14,
    },
    editModalInput: {
        minHeight: 80,
        maxHeight: 220,
        borderWidth: 1,
        borderColor: '#d0d4dc',
        borderRadius: 10,
        padding: 12,
        fontFamily: 'Inter',
        fontSize: 15,
        color: COLORS.deepNavy ?? '#1c2228',
        textAlignVertical: 'top',
        marginBottom: 16,
    },
    editModalActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
    },
    editModalBtnCancel: {
        paddingVertical: 10,
        paddingHorizontal: 18,
    },
    editModalBtnCancelText: {
      lineHeight: 22,
      fontFamily: 'Inter',
      fontSize: 16,
      fontWeight: '500',
        color: COLORS.blueSteel,
    },
    editModalBtnSave: {
        backgroundColor: COLORS.primaryNavy ?? '#2c3e50',
        paddingVertical: 10,
        paddingHorizontal: 22,
        borderRadius: 10,
        minWidth: 84,
        alignItems: 'center',
    },
    editModalBtnSaveDisabled: {
        opacity: 0.5,
    },
    editModalBtnSaveText: {
      lineHeight: 22,
      fontFamily: 'Inter',
      fontSize: 16,
      fontWeight: '500',
        color: '#fff',
    },
});

export default SupportScreen;
